/**
 * MobileAccountList Component
 *
 * Mobile page for displaying the list of trading accounts.
 * Route: /settings/portfolio (mobile only, index route)
 *
 * Pattern matches MobileBrokerList:
 * - Reference Data Card at top (Brokers, Instruments)
 * - Main List Card with Archive toggle and New button (popover)
 * - Broker dependency gate on "Create New..." action
 */

// 1. External dependencies
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye, EyeOff, Plus } from 'lucide-react';

// 2. Workspace packages
import {
  Button,
  Card,
  CardHeader,
  CardBody,
  EmptyState,
  Popover,
  PopoverTrigger,
  PopoverContent,
} from '@enfomentis/ui';

// 3. Internal — relative imports
import { useAccounts } from '../context';
import { AccountListItem } from '../components';

export const MobileAccountList: React.FC = () => {
  const navigate = useNavigate();

  const {
    accounts,
    filteredAccounts,
    loading,
    showArchived,
    setShowArchived,
    activeBrokers,
    brokersLoading,
  } = useAccounts();

  // Popover state for "New" button (matching MobileBrokerList pattern)
  const [isAddPopoverOpen, setIsAddPopoverOpen] = useState(false);

  // Empty state popover (separate instance)
  const [isEmptyPopoverOpen, setIsEmptyPopoverOpen] = useState(false);

  // ---------------------------------------------------------------------------
  // Handlers
  // ---------------------------------------------------------------------------

  const handleAccountSelect = (accountId: string): void => {
    navigate(`/settings/portfolio/${accountId}`);
  };

  const handleShowArchivedToggle = (): void => {
    setShowArchived(!showArchived);
  };

  const handleNewAccount = (): void => {
    setIsAddPopoverOpen(false);
    setIsEmptyPopoverOpen(false);
    navigate('/settings/portfolio/new');
  };

  // ---------------------------------------------------------------------------
  // Derived state
  // ---------------------------------------------------------------------------

  const hasAccounts = accounts.length > 0;
  const hasFilteredAccounts = filteredAccounts.length > 0;
  const hasBrokers = activeBrokers.length > 0;
  const canCreate = hasBrokers || brokersLoading;

  // ---------------------------------------------------------------------------
  // Shared popover content (used by both header and empty state popovers)
  // ---------------------------------------------------------------------------

  const renderPopoverButtons = (): React.ReactNode => (
    <PopoverContent className="p-1">
      <div className="flex flex-col min-w-40">
        <Button
          size="md"
          variant="light"
          className="justify-start"
          startContent={<Plus size={16} />}
          onPress={handleNewAccount}
          isDisabled={!canCreate}
        >
          Create New...
        </Button>
        {!hasBrokers && !brokersLoading && (
          <p className="text-text-tertiary text-xs px-3 pb-2">
            Create a broker first
          </p>
        )}
      </div>
    </PopoverContent>
  );

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <div className="flex flex-col h-full gap-3">
      {/* Reference Data Card */}
      <Card radius="none" shadow="none" className="border-1 border-default-200">
        <CardBody className="py-1 px-3">
          <div className="flex justify-evenly">
            <Button
              size="sm"
              variant="light"
              onPress={() => navigate('/settings/portfolio/reference-data/brokers')}
              className="w-30"
            >
              Brokers
            </Button>
            <Button
              size="sm"
              variant="light"
              onPress={() => navigate('/settings/portfolio/reference-data/instruments')}
              className="w-30"
            >
              Instruments
            </Button>
          </div>
        </CardBody>
      </Card>

      {/* Main Account List Card */}
      <Card radius="none" shadow="none" className="flex-1 min-h-0 flex flex-col">
        {/* Header */}
        <CardHeader className="flex items-center justify-between pb-1 border-b border-default-200">
          {/* Left: Archive toggle */}
          <Button
            size="sm"
            variant="light"
            startContent={showArchived ? <Eye size={16} /> : <EyeOff size={16} />}
            onPress={handleShowArchivedToggle}
            className={showArchived ? 'text-text-primary' : 'text-text-tertiary'}
          >
            Archived
          </Button>

          {/* Right: New button with Popover */}
          <Popover
            isOpen={isAddPopoverOpen}
            onOpenChange={setIsAddPopoverOpen}
            placement="bottom-end"
          >
            <PopoverTrigger>
              <Button
                size="sm"
                variant="light"
                startContent={<Plus size={16} />}
              >
                New
              </Button>
            </PopoverTrigger>
            {renderPopoverButtons()}
          </Popover>
        </CardHeader>

        {/* Content */}
        <CardBody className="flex-1 min-h-0 overflow-y-auto px-1 pb-2">
          {/* Loading state */}
          {loading && (
            <div className="text-center py-8">
              <p className="text-text-secondary">Loading accounts...</p>
            </div>
          )}

          {/* Empty state - No accounts */}
          {!hasAccounts && !loading && (
            <EmptyState
              title="No accounts yet"
              description="Create your first account to get started."
              action={
                <Popover
                  isOpen={isEmptyPopoverOpen}
                  onOpenChange={setIsEmptyPopoverOpen}
                  placement="bottom"
                >
                  <PopoverTrigger>
                    <Button
                      variant="bordered"
                      startContent={<Plus size={16} />}
                    >
                      New Account
                    </Button>
                  </PopoverTrigger>
                  {renderPopoverButtons()}
                </Popover>
              }
            />
          )}

          {/* Empty state - All archived */}
          {hasAccounts && !hasFilteredAccounts && !loading && (
            <EmptyState
              title="No active accounts"
              description="All accounts are archived"
              action={
                <Button
                  variant="flat"
                  startContent={<Eye size={16} />}
                  onPress={handleShowArchivedToggle}
                >
                  Show Archived
                </Button>
              }
            />
          )}

          {/* Account list */}
          {!loading && hasFilteredAccounts && (
            <div className="space-y-2">
              {filteredAccounts.map((account) => (
                <AccountListItem
                  key={account.id}
                  account={account}
                  isSelected={false}
                  onSelect={() => handleAccountSelect(account.id)}
                />
              ))}
            </div>
          )}
        </CardBody>
      </Card>
    </div>
  );
};
